<template>
  <div class="w-full relative">
    <label v-if="label" :for="name" class="block mb-1 text-sm font-medium text-gray-700">
      {{ label }} <span v-if="required && !disabled" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <textarea
        :id="name"
        :name="name"
        rows="4"
        v-model="internalValue"
        :placeholder="placeholder"
        :maxlength="length"
        :disabled="disabled"
        :class="[baseClass, error ? 'border-red-500' : 'border-gray-600', customClass]"
        @blur="onBlur"
        @input="onInput"
      ></textarea>
    </div>

    <p v-if="error && !disabled" :class="['text-xs -mt-[3px] mb-0', errorClass ? errorClass : 'text-red-500']">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, watch, defineExpose } from 'vue';

const props = defineProps({
  modelValue: {},
  name: String,
  label: String,
  placeholder: String,
  required: {
    type: Boolean,
    default: false
  },
  regex: RegExp,
  errorMessage: String,
  customClass: {
    type: String,
    default: ''
  },
  errorClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  confirmWith: {
    type: String,
    default: ''
  },
  length: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'input']);

const internalValue = ref(props.modelValue || '');
const error = ref('');
const touched = ref(false);

const baseClass = 'w-full p-2 rounded-md border outline-none text-sm !text-[#333] disabled:cursor-not-allowed disabled:!bg-[#EFEFEF] disabled:!text-[#666]';

watch(() => props.modelValue, (val) => {
  internalValue.value = val || '';
});

watch(internalValue, (val) => {
  emit('update:modelValue', val);
  if (touched.value && !props.disabled) {
    validate();
  }
});

function onBlur() {
  touched.value = true;
  validate();
}

function onInput(event) {
  let value = event.target.value;
  emit("input", value);
  validate();
}

function validate() {
  if (props.disabled) return '';
  let trimmed = internalValue.value?.trim() || '';

  if (props.required && trimmed === '') {
    error.value = 'This field is required.';
    return error.value;
  }

  if (trimmed !== '' && props.regex && !props.regex.test(trimmed)) {
    error.value = props.errorMessage || 'Invalid input.';
    return error.value;
  }

  if (props.confirmWith && props.confirmWith !== trimmed) {
    error.value = props.errorMessage || 'Values do not match.';
    return error.value;
  }

  error.value = '';
  return '';
}

function resetField() {
  error.value = ''
}

defineExpose({
  resetField,
  validate,
  hasError: () => !!error.value
});
</script>

<style scoped>
textarea {
  resize: vertical; /* Optional: allow vertical resize only */
}
</style>
